import { Storage } from '@airgap/beacon-types';
import { Logger } from '../utils/Logger';
const logger = new Logger('IndexedDBStorage');
export class IndexedDBStorage extends Storage {
    dbName;
    storeNames;
    db = null;
    isSupported = true;
    /**
     * @param dbName Name of the database.
     * @param storeNames An array of object store names to create in the database.
     *                   The first store in the array will be used as the default if none is specified.
     */
    constructor(dbName = 'WALLET_CONNECT_V2_INDEXED_DB', storeNames = ['keyvaluestorage']) {
        super();
        this.dbName = dbName;
        this.storeNames = storeNames;
        this.initDB()
            .then((db) => (this.db = db))
            .catch((err) => logger.error(err.message));
    }
    isIndexedDBSupported() {
        if (typeof window !== 'undefined' && 'indexedDB' in window) {
            logger.log('isIndexedDBSupported', 'IndexedDB is supported in this browser.');
            return true;
        }
        else {
            logger.error('isIndexedDBSupported', 'IndexedDB is not supported in this browser.');
            return false;
        }
    }
    async initDB() {
        return new Promise((resolve, reject) => {
            this.isSupported = this.isIndexedDBSupported();
            if (!this.isSupported) {
                reject('IndexedDB is not supported.');
                return;
            }
            const openRequest = indexedDB.open(this.dbName);
            openRequest.onupgradeneeded = () => {
                const db = openRequest.result;
                // Create all required object stores
                this.storeNames.forEach((storeName) => {
                    if (!db.objectStoreNames.contains(storeName)) {
                        db.createObjectStore(storeName);
                    }
                });
            };
            openRequest.onsuccess = (event) => {
                const db = event.target.result;
                // Check if all stores exist – if not, perform an upgrade.
                const missingStores = this.storeNames.filter((storeName) => !db.objectStoreNames.contains(storeName));
                if (missingStores.length > 0) {
                    db.close();
                    const newVersion = db.version + 1;
                    const upgradeRequest = indexedDB.open(this.dbName, newVersion);
                    upgradeRequest.onupgradeneeded = () => {
                        const upgradedDB = upgradeRequest.result;
                        missingStores.forEach((storeName) => {
                            if (!upgradedDB.objectStoreNames.contains(storeName)) {
                                upgradedDB.createObjectStore(storeName);
                            }
                        });
                    };
                    upgradeRequest.onsuccess = (event) => {
                        this.db = event.target.result;
                        resolve(this.db);
                    };
                    upgradeRequest.onerror = (event) => reject(event.target.error);
                }
                else {
                    this.db = db;
                    resolve(db);
                }
            };
            openRequest.onerror = (event) => reject(event.target.error);
        });
    }
    /**
     * Performs a transaction on the given object store.
     * @param mode Transaction mode.
     * @param storeName The name of the object store.
     * @param operation The operation to perform with the object store.
     */
    async transaction(mode, storeName, operation) {
        return new Promise((resolve, reject) => {
            if (!this.isSupported) {
                reject('IndexedDB is not supported.');
                return;
            }
            if (!this.db?.objectStoreNames.contains(storeName)) {
                reject(`${storeName} not found. error: ${new Error().stack}`);
                return;
            }
            const transaction = this.db.transaction(storeName, mode);
            const objectStore = transaction.objectStore(storeName);
            operation(objectStore).then(resolve).catch(reject);
        });
    }
    get(key, storeName = this.storeNames[0]) {
        return this.transaction('readonly', storeName, (store) => new Promise((resolve, reject) => {
            const getRequest = store.get(key);
            getRequest.onsuccess = () => resolve(getRequest.result);
            getRequest.onerror = () => reject(getRequest.error);
        }));
    }
    set(key, value, storeName = this.storeNames[0]) {
        return this.transaction('readwrite', storeName, (store) => new Promise((resolve, reject) => {
            const putRequest = store.put(value, key);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
        }));
    }
    delete(key, storeName = this.storeNames[0]) {
        return this.transaction('readwrite', storeName, (store) => new Promise((resolve, reject) => {
            const deleteRequest = store.delete(key);
            deleteRequest.onsuccess = () => resolve();
            deleteRequest.onerror = () => reject(deleteRequest.error);
        }));
    }
    /**
     * Retrieves all values from the specified object store.
     */
    getAll(storeName) {
        return this.transaction('readonly', storeName || this.storeNames[0], (store) => new Promise((resolve, reject) => {
            const getAllRequest = store.getAll();
            getAllRequest.onsuccess = () => resolve(getAllRequest.result);
            getAllRequest.onerror = () => reject(getAllRequest.error);
        }));
    }
    /**
     * Retrieves all keys from the specified object store.
     */
    getAllKeys(storeName) {
        return this.transaction('readonly', storeName || this.storeNames[0], (store) => new Promise((resolve, reject) => {
            const getAllKeysRequest = store.getAllKeys();
            getAllKeysRequest.onsuccess = () => resolve(getAllKeysRequest.result);
            getAllKeysRequest.onerror = () => reject(getAllKeysRequest.error);
        }));
    }
    /**
     * Clears all entries from the specified object store.
     */
    clearStore(storeName) {
        return this.transaction('readwrite', storeName || this.storeNames[0], (store) => new Promise((resolve, reject) => {
            const clearRequest = store.clear();
            clearRequest.onsuccess = () => resolve();
            clearRequest.onerror = () => reject(clearRequest.error);
        }));
    }
    getPrefixedKey(key) {
        logger.debug('getPrefixedKey', key);
        throw new Error('Method not implemented.');
    }
    subscribeToStorageChanged(callback) {
        logger.debug('subscribeToStorageEvent', callback);
        throw new Error('Method not implemented.');
    }
    /**
     * Copies all key/value pairs from the source store into a target store.
     * @param targetDBName Name of the target database.
     * @param targetStoreName Name of the target object store.
     * @param skipKeys Keys to skip.
     * @param sourceStoreName (Optional) Source store name – defaults to the default store.
     */
    async fillStore(targetDBName, targetStoreName, skipKeys = [], sourceStoreName = this.storeNames[0]) {
        if (!this.isSupported) {
            logger.error('fillStore', 'IndexedDB not supported.');
            return;
        }
        const targetDBRequest = indexedDB.open(targetDBName);
        targetDBRequest.onerror = (event) => {
            throw new Error(`Failed to open target database: ${event.target.error}`);
        };
        const targetDB = await new Promise((resolve, reject) => {
            targetDBRequest.onsuccess = (event) => resolve(event.target.result);
            targetDBRequest.onerror = (event) => reject(event.target.error);
        });
        // Copy items from the source store to the target store, skipping any specified keys.
        await this.transaction('readonly', sourceStoreName, async (sourceStore) => {
            const getAllRequest = sourceStore.getAll();
            const getAllKeysRequest = sourceStore.getAllKeys();
            getAllRequest.onsuccess = async () => {
                getAllKeysRequest.onsuccess = async () => {
                    const items = getAllRequest.result;
                    const keys = getAllKeysRequest.result;
                    if (!targetDB.objectStoreNames.contains(targetStoreName)) {
                        logger.error(`${targetStoreName} not found. ${new Error().stack}`);
                        return;
                    }
                    const targetTransaction = targetDB.transaction(targetStoreName, 'readwrite');
                    const targetStore = targetTransaction.objectStore(targetStoreName);
                    keys
                        .filter((key) => !skipKeys.includes(key.toString()))
                        .forEach((key, index) => {
                        targetStore.put(items[index], key);
                    });
                    targetTransaction.onerror = (event) => {
                        logger.error('Transaction error: ', event.target.error);
                    };
                };
            };
            getAllKeysRequest.onerror = () => {
                logger.error('Failed to getAllKeys from source:', getAllKeysRequest.error);
            };
            getAllRequest.onerror = () => {
                logger.error('Failed to getAll from source:', getAllRequest.error);
            };
        });
    }
}
//# sourceMappingURL=IndexedDBStorage.js.map